import { z } from "zod";

// The controlled vocabularies of the UPS sizing module (EOS-03c), copied
// verbatim from the backend so a value the API can send is never rejected
// here. Sources: backend/app/domain/electrical/sources/ups_results.py
// (UPSSizingStatus, UPSSizingWarningCode, UPSGoverningBasis, UPSEnergyBasis,
// NOT_ESTABLISHED_WARNING_CODES) and .../sources/ups_models.py (UPSTopology,
// UPSPhaseConfiguration, UPSRedundancyMode, UPSBatteryTechnology).
// upsSizingContract.test.ts pins each list.
//
// The operating scenario is the same enum the load module uses, so it is reused
// from loadDemandContract rather than written out a second time.

export const upsSizingStatusSchema = z.enum([
  "VALID",
  "WARNING",
  "REVIEW_REQUIRED",
  "NO_SOLUTION",
]);

export const upsSizingWarningCodeSchema = z.enum([
  "DERATING_APPLIED",
  "ACTIVE_POWER_REQUIREMENT_GOVERNS",
  "BATTERY_SIZING_NOT_EVALUATED",
  "NO_STANDARD_RATING_AVAILABLE",
  "GROWTH_FACTOR_NOT_ESTABLISHED",
  "DESIGN_MARGIN_NOT_ESTABLISHED",
  "AMBIENT_DERATING_NOT_ESTABLISHED",
  "ALTITUDE_DERATING_NOT_ESTABLISHED",
  "INVERTER_EFFICIENCY_NOT_ESTABLISHED",
]);

export const upsTopologySchema = z.enum([
  "OFFLINE",
  "LINE_INTERACTIVE",
  "ONLINE_DOUBLE_CONVERSION",
]);

export const upsPhaseConfigurationSchema = z.enum(["SINGLE_PHASE", "THREE_PHASE"]);

export const upsRedundancyModeSchema = z.enum([
  "NONE",
  "N_PLUS_1",
  "TWO_N",
]);

export const upsBatteryTechnologySchema = z.enum(["VRLA", "LITHIUM_ION", "NICKEL_CADMIUM"]);

/** Which of the two rating limits governed the selection (A18 (c)). */
export const upsGoverningBasisSchema = z.enum(["KVA", "KW"]);

/** What the load energy is computed from (A18 (e)); stated on every result. */
export const upsEnergyBasisSchema = z.enum(["DESIGN_KW_INCLUDING_GROWTH_AND_MARGIN"]);

/** The five warnings that make a result REVIEW_REQUIRED rather than WARNING. */
export const NOT_ESTABLISHED_WARNING_CODES = [
  "GROWTH_FACTOR_NOT_ESTABLISHED",
  "DESIGN_MARGIN_NOT_ESTABLISHED",
  "AMBIENT_DERATING_NOT_ESTABLISHED",
  "ALTITUDE_DERATING_NOT_ESTABLISHED",
  "INVERTER_EFFICIENCY_NOT_ESTABLISHED",
] as const;

/**
 * Carried by every UPS result (A18 (e), GAP-018): cell count, Ah, end-of-discharge
 * voltage, ageing and temperature correction are not evaluated. It gives WARNING,
 * never REVIEW_REQUIRED.
 */
export const BATTERY_SIZING_NOT_EVALUATED = "BATTERY_SIZING_NOT_EVALUATED";

/** The choice the engineer must make; the backend imposes no default (A18 (h)). */
export const REQUIRED_UPS_CHOICES = ["redundancy_mode"] as const;

/**
 * Descriptors: optional, no default, echoed in the result, no effect on any number
 * (A18 (h)).
 */
export const UPS_DESCRIPTORS = [
  "topology",
  "phase_configuration",
  "battery_technology",
  "scenario",
] as const;

export type UPSSizingStatus = z.infer<typeof upsSizingStatusSchema>;
export type UPSSizingWarningCode = z.infer<typeof upsSizingWarningCodeSchema>;
export type UPSTopology = z.infer<typeof upsTopologySchema>;
export type UPSPhaseConfiguration = z.infer<typeof upsPhaseConfigurationSchema>;
export type UPSRedundancyMode = z.infer<typeof upsRedundancyModeSchema>;
export type UPSBatteryTechnology = z.infer<typeof upsBatteryTechnologySchema>;
export type UPSGoverningBasis = z.infer<typeof upsGoverningBasisSchema>;
export type UPSEnergyBasis = z.infer<typeof upsEnergyBasisSchema>;
